import { z } from 'zod';
import { CountryCodeSchema } from './geo';
import { MenuFoodTypeSchema } from './menu';

/* ------------------------------------------------------------------ */
/* Orders + kitchen (W7)                                               */
/*                                                                     */
/* The order core the Partner app runs on. Customer checkout (W10)     */
/* and dispatch (W13) plug into the same pipeline later; until then    */
/* Ops create test orders and move the rider along from Capsico OS.    */
/*                                                                     */
/* Kitchen statuses:                                                   */
/*   placed     waiting for the restaurant to accept (manual mode)     */
/*   preparing  accepted, cooking (auto-accept lands here at once)     */
/*   ready      food packed, seal scanned, waiting for the rider       */
/*   picked_up  rider left the restaurant                              */
/*   delivered  done — counts toward the outlet's plan usage           */
/*   cancelled  not accepted in time, rejected, or cancelled by Ops    */
/*                                                                     */
/* Every timer is server-driven (accept deadline, prep time); apps     */
/* only count down from the times the server sends.                    */
/* ------------------------------------------------------------------ */

export const ORDER_STATUSES = [
  'placed',
  'preparing',
  'ready',
  'picked_up',
  'delivered',
  'cancelled',
] as const;
export const OrderStatusSchema = z.enum(ORDER_STATUSES);
export type OrderStatus = z.infer<typeof OrderStatusSchema>;
export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  placed: 'New',
  preparing: 'Preparing',
  ready: 'Ready',
  picked_up: 'Picked up',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
};

export const ORDER_SOURCES = ['app', 'test'] as const;
export const OrderSourceSchema = z.enum(ORDER_SOURCES);
export type OrderSource = z.infer<typeof OrderSourceSchema>;

export const CANCEL_REASONS = [
  'not_accepted',
  'rejected',
  'ops',
  'customer',
  'unpaid',
  'no_rider',
] as const;
export const CancelReasonSchema = z.enum(CANCEL_REASONS);
export type CancelReason = z.infer<typeof CancelReasonSchema>;
export const CANCEL_REASON_LABELS: Record<CancelReason, string> = {
  not_accepted: 'Not accepted in time',
  rejected: 'Rejected by restaurant',
  ops: 'Cancelled by Capsico',
  customer: 'Cancelled by customer',
  unpaid: 'Payment not completed',
  no_rider: 'No rider found',
};

export const REJECT_REASONS = [
  'out_of_stock',
  'kitchen_overloaded',
  'closing_early',
  'other',
] as const;
export const RejectReasonSchema = z.enum(REJECT_REASONS);
export type RejectReason = z.infer<typeof RejectReasonSchema>;
export const REJECT_REASON_LABELS: Record<RejectReason, string> = {
  out_of_stock: 'Item out of stock',
  kitchen_overloaded: 'Kitchen overloaded',
  closing_early: 'Closing early',
  other: 'Something else',
};

/** Where the rider is. Simulated from Capsico OS until dispatch (W13) exists. */
export const RIDER_STATES = [
  'searching',
  'assigned',
  'at_restaurant',
  'picked_up',
  'delivered',
] as const;
export const RiderStateSchema = z.enum(RIDER_STATES);
export type RiderState = z.infer<typeof RiderStateSchema>;

/** New-order alert chain: in-app ring first, then WhatsApp and a phone call (seams). */
export const ALERT_CHANNELS = ['push', 'whatsapp', 'ivr'] as const;
export const AlertChannelSchema = z.enum(ALERT_CHANNELS);
export type AlertChannel = z.infer<typeof AlertChannelSchema>;
export const ALERT_RESULTS = ['sent', 'skipped', 'failed'] as const;
export type AlertResult = (typeof ALERT_RESULTS)[number];

export const ACCEPT_MODES = ['auto', 'manual'] as const;
export const AcceptModeSchema = z.enum(ACCEPT_MODES);
export type AcceptMode = z.infer<typeof AcceptModeSchema>;
export const ACCEPT_MODE_LABELS: Record<AcceptMode, string> = {
  auto: 'Auto-accept',
  manual: 'Manual',
};

export const OFFLINE_REASONS = [
  'owner',
  'closing_after_orders',
  'missed_orders',
  'paused_by_ops',
] as const;
export const OfflineReasonSchema = z.enum(OFFLINE_REASONS);
export type OfflineReason = z.infer<typeof OfflineReasonSchema>;
export const OFFLINE_REASON_LABELS: Record<OfflineReason, string> = {
  owner: 'Turned off by you',
  closing_after_orders: 'Closing after live orders',
  missed_orders: 'Turned off after a missed order',
  paused_by_ops: 'Paused by Capsico',
};

/* ------------------------------------------------------------------ */
/* Views                                                               */
/* ------------------------------------------------------------------ */

export const OrderAddonSchema = z.object({ name: z.string(), priceMinor: z.number().int() });
export type OrderAddon = z.infer<typeof OrderAddonSchema>;

export const OrderLineSchema = z.object({
  id: z.uuid(),
  menuItemId: z.uuid().nullable(),
  name: z.string(),
  foodType: MenuFoodTypeSchema,
  variantName: z.string().nullable(),
  addons: z.array(OrderAddonSchema),
  quantity: z.number().int(),
  unitPriceMinor: z.number().int(),
  lineTotalMinor: z.number().int(),
  note: z.string().nullable(),
  outOfStock: z.boolean(),
});
export type OrderLine = z.infer<typeof OrderLineSchema>;

export const OrderRiderSchema = z.object({
  state: RiderStateSchema,
  name: z.string().nullable(),
  phoneMasked: z.string().nullable(),
  /** Minutes until the rider reaches the restaurant, when known. */
  etaMinutes: z.number().int().nullable(),
  /** 4-digit pickup passcode the rider reads out at the counter. */
  passcode: z.string().nullable(),
  arrivedAt: z.string().nullable(),
});
export type OrderRider = z.infer<typeof OrderRiderSchema>;

export const OrderBillSchema = z.object({
  itemTotalMinor: z.number().int(),
  packagingMinor: z.number().int(),
  taxMinor: z.number().int(),
  taxLabel: z.string().nullable(),
  totalMinor: z.number().int(),
});
export type OrderBill = z.infer<typeof OrderBillSchema>;

export const OrderEventSchema = z.object({
  id: z.string(),
  action: z.string(),
  summary: z.string(),
  actorName: z.string().nullable(),
  at: z.string(),
});
export type OrderEvent = z.infer<typeof OrderEventSchema>;

export const OrderRowSchema = z.object({
  id: z.uuid(),
  /** Short number shown in kitchens, e.g. "5991". */
  number: z.string(),
  outletId: z.uuid(),
  outletName: z.string(),
  restaurantName: z.string(),
  townName: z.string(),
  country: CountryCodeSchema,
  source: OrderSourceSchema,
  status: OrderStatusSchema,
  foodType: z.enum(['veg', 'non_veg', 'mixed']),
  customerName: z.string(),
  itemCount: z.number().int(),
  itemsSummary: z.string(),
  totalMinor: z.number().int(),
  placedAt: z.string(),
  scheduledFor: z.string().nullable(),
  /** Placed orders: accept before this or the order is cancelled. */
  acceptBy: z.string().nullable(),
  acceptedAt: z.string().nullable(),
  acceptedBy: z.enum(['auto', 'restaurant', 'ops']).nullable(),
  prepMinutes: z.number().int().nullable(),
  /** Preparing orders: food should be ready by this time. */
  readyBy: z.string().nullable(),
  readyAt: z.string().nullable(),
  pickedUpAt: z.string().nullable(),
  deliveredAt: z.string().nullable(),
  cancelledAt: z.string().nullable(),
  cancelReason: CancelReasonSchema.nullable(),
  rejectReason: RejectReasonSchema.nullable(),
  sealCode: z.string().nullable(),
  sealMissingReason: z.string().nullable(),
  rider: OrderRiderSchema,
  version: z.number().int(),
  updatedAt: z.string(),
});
export type OrderRow = z.infer<typeof OrderRowSchema>;

export const OrderDetailSchema = OrderRowSchema.extend({
  customerPhoneMasked: z.string().nullable(),
  customerNote: z.string().nullable(),
  cutlery: z.boolean(),
  lines: z.array(OrderLineSchema),
  bill: OrderBillSchema,
  rejectNote: z.string().nullable(),
  penaltyMinor: z.number().int(),
  events: z.array(OrderEventSchema),
  /** The prep time Capsico suggests, from the dishes and how busy the kitchen is. */
  suggestedPrepMinutes: z.number().int(),
  canAct: z.boolean(),
});
export type OrderDetail = z.infer<typeof OrderDetailSchema>;

/* ------------------------------------------------------------------ */
/* Kitchen (one per outlet)                                            */
/* ------------------------------------------------------------------ */

export const KitchenStateSchema = z.object({
  outletId: z.uuid(),
  online: z.boolean(),
  offlineReason: OfflineReasonSchema.nullable(),
  /** Set when the outlet turns itself back on automatically. */
  offlineUntil: z.string().nullable(),
  acceptMode: AcceptModeSchema,
  /** Rush hour: minutes added to every prep estimate, and until when. */
  rushMinutes: z.number().int(),
  rushUntil: z.string().nullable(),
  version: z.number().int(),
});
export type KitchenState = z.infer<typeof KitchenStateSchema>;

export const KitchenSettingsSchema = z.object({
  acceptRingMinutes: z.number().int(),
  acceptIvrAfterMinutes: z.number().int(),
  acceptAutoCancelMinutes: z.number().int(),
  rushStepMinutes: z.number().int(),
  maxPauseHours: z.number().int(),
  rejectPenaltyMinor: z.number().int(),
  missedOrderFeeMinor: z.number().int(),
  sealRequired: z.boolean(),
});
export type KitchenSettings = z.infer<typeof KitchenSettingsSchema>;

export const PartnerOutletSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  restaurantName: z.string(),
  locality: z.string(),
  townName: z.string(),
  country: CountryCodeSchema,
  timezone: z.string(),
  status: z.string(),
  kitchen: KitchenStateSchema,
  counts: z.object({
    placed: z.number().int(),
    preparing: z.number().int(),
    ready: z.number().int(),
    pickedUp: z.number().int(),
    scheduled: z.number().int(),
  }),
});
export type PartnerOutlet = z.infer<typeof PartnerOutletSchema>;

export const PartnerOutletsSchema = z.object({
  outlets: z.array(PartnerOutletSchema),
  settings: KitchenSettingsSchema,
  serverTime: z.string(),
});
export type PartnerOutlets = z.infer<typeof PartnerOutletsSchema>;

/* ------------------------------------------------------------------ */
/* Lists                                                               */
/* ------------------------------------------------------------------ */

/** Partner board tabs. "new" = waiting to be accepted (manual mode). */
export const BOARD_VIEWS = ['new', 'preparing', 'ready', 'picked_up', 'scheduled', 'past'] as const;
export const BoardViewSchema = z.enum(BOARD_VIEWS);
export type BoardView = z.infer<typeof BoardViewSchema>;
export const BOARD_VIEW_LABELS: Record<BoardView, string> = {
  new: 'New',
  preparing: 'Preparing',
  ready: 'Ready',
  picked_up: 'Picked up',
  scheduled: 'Scheduled',
  past: 'Past orders',
};

export const PartnerBoardQuerySchema = z.object({
  outletId: z.uuid(),
  view: BoardViewSchema.default('preparing'),
});
export type PartnerBoardQuery = z.infer<typeof PartnerBoardQuerySchema>;

export const BoardSchema = z.object({
  rows: z.array(OrderRowSchema),
  /** Every order waiting to be accepted, whatever tab is open — drives the ringing alert. */
  incoming: z.array(OrderRowSchema),
  counts: z.record(BoardViewSchema, z.number().int()),
  kitchen: KitchenStateSchema,
  serverTime: z.string(),
});
export type Board = z.infer<typeof BoardSchema>;

export const PulseQuerySchema = z.object({ outletId: z.uuid() });
export const PulseSchema = z.object({
  /** Changes whenever any order or the kitchen of this outlet changes. */
  token: z.string(),
  incoming: z.number().int(),
  serverTime: z.string(),
});
export type Pulse = z.infer<typeof PulseSchema>;

export const OPS_ORDER_VIEWS = ['live', 'waiting', 'late', 'done', 'cancelled', 'all'] as const;
export const OpsOrderViewSchema = z.enum(OPS_ORDER_VIEWS);
export type OpsOrderView = z.infer<typeof OpsOrderViewSchema>;
export const OPS_ORDER_VIEW_LABELS: Record<OpsOrderView, string> = {
  live: 'Live',
  waiting: 'Waiting to accept',
  late: 'Running late',
  done: 'Delivered',
  cancelled: 'Cancelled',
  all: 'All',
};

export const OrderListQuerySchema = z.object({
  view: OpsOrderViewSchema.default('live'),
  townId: z.uuid().optional(),
  country: CountryCodeSchema.optional(),
  outletId: z.uuid().optional(),
  search: z.string().trim().max(80).optional(),
  limit: z.coerce.number().int().min(1).max(500).default(200),
});
export type OrderListQuery = z.infer<typeof OrderListQuerySchema>;

export const OrderListSchema = z.object({
  rows: z.array(OrderRowSchema),
  counts: z.record(OpsOrderViewSchema, z.number().int()),
  /** Development only: "Send test order" and the rider simulator are available. */
  testTools: z.boolean(),
  serverTime: z.string(),
});
export type OrderList = z.infer<typeof OrderListSchema>;

/* ------------------------------------------------------------------ */
/* Inputs                                                              */
/* ------------------------------------------------------------------ */

const Version = z.number().int().min(1);

export const MIN_PREP_MINUTES = 1;
export const MAX_PREP_MINUTES = 120;

export const AcceptOrderInputSchema = z.object({
  expectedVersion: Version,
  prepMinutes: z.number().int().min(MIN_PREP_MINUTES).max(MAX_PREP_MINUTES).optional(),
});
export type AcceptOrderInput = z.infer<typeof AcceptOrderInputSchema>;

export const RejectOrderInputSchema = z
  .object({
    expectedVersion: Version,
    reason: RejectReasonSchema,
    /** Out of stock: the order lines that ran out. Those dishes switch off until the daily reset. */
    lineIds: z.array(z.uuid()).max(50).default([]),
    note: z.string().trim().max(300).nullable().default(null),
  })
  .refine((v) => v.reason !== 'out_of_stock' || v.lineIds.length > 0, {
    message: 'Pick the dishes that ran out',
    path: ['lineIds'],
  })
  .refine((v) => v.reason !== 'other' || (v.note ?? '').length >= 3, {
    message: 'Tell us what happened',
    path: ['note'],
  });
export type RejectOrderInput = z.infer<typeof RejectOrderInputSchema>;

export const AddPrepTimeInputSchema = z.object({
  expectedVersion: Version,
  addMinutes: z.number().int().min(1).max(60),
});
export type AddPrepTimeInput = z.infer<typeof AddPrepTimeInputSchema>;

/** Serial-numbered tamper tape, e.g. CAPS-4091. */
export const SealCodeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]{2,6}-?\d{3,10}$/, 'Scan or type the code on the seal tape');

export const MarkReadyInputSchema = z
  .object({
    expectedVersion: Version,
    sealCode: SealCodeSchema.nullable().default(null),
    sealMissingReason: z.string().trim().min(3).max(200).nullable().default(null),
  })
  .refine((v) => v.sealCode === null || v.sealMissingReason === null, {
    message: 'Either scan the seal or say why it is missing',
    path: ['sealMissingReason'],
  });
export type MarkReadyInput = z.infer<typeof MarkReadyInputSchema>;

export const MarkOutOfStockInputSchema = z.object({
  expectedVersion: Version,
  lineIds: z.array(z.uuid()).min(1, 'Pick at least one dish').max(50),
});
export type MarkOutOfStockInput = z.infer<typeof MarkOutOfStockInputSchema>;

export const KITCHEN_ACTIONS = [
  'go_online',
  'go_offline',
  'close_after_orders',
  'accept_mode',
  'rush',
  'end_rush',
] as const;
export const KitchenUpdateInputSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('go_online'), expectedVersion: Version }),
  z.object({
    action: z.literal('go_offline'),
    expectedVersion: Version,
    /** Turn back on automatically after this many minutes; null = until turned on. */
    forMinutes: z
      .number()
      .int()
      .min(15)
      .max(72 * 60)
      .nullable()
      .default(null),
  }),
  z.object({ action: z.literal('close_after_orders'), expectedVersion: Version }),
  z.object({ action: z.literal('accept_mode'), expectedVersion: Version, mode: AcceptModeSchema }),
  z.object({
    action: z.literal('rush'),
    expectedVersion: Version,
    /** 1–3 steps of the kitchen-busy step (10 min each by default). */
    steps: z.number().int().min(1).max(3),
    forMinutes: z.union([z.literal(30), z.literal(60), z.literal(120)]),
  }),
  z.object({ action: z.literal('end_rush'), expectedVersion: Version }),
]);
export type KitchenUpdateInput = z.infer<typeof KitchenUpdateInputSchema>;

export const PUSH_PLATFORMS = ['android', 'web'] as const;
export const RegisterDeviceInputSchema = z.object({
  token: z.string().trim().min(20).max(4096),
  platform: z.enum(PUSH_PLATFORMS),
});
export type RegisterDeviceInput = z.infer<typeof RegisterDeviceInputSchema>;

/* Ops (Capsico OS) */

export const TestOrderInputSchema = z.object({
  outletId: z.uuid(),
  /** How many dishes to pick at random from the live menu. */
  dishes: z.number().int().min(1).max(6).default(2),
  /** Minutes from now; null = deliver now. */
  scheduleInMinutes: z
    .number()
    .int()
    .min(30)
    .max(24 * 60)
    .nullable()
    .default(null),
  customerNote: z.string().trim().max(200).nullable().default(null),
});
export type TestOrderInput = z.infer<typeof TestOrderInputSchema>;

export const RIDER_STEPS = ['assign', 'arrive', 'pick_up', 'deliver'] as const;
export const RiderStepInputSchema = z.object({
  expectedVersion: Version,
  step: z.enum(RIDER_STEPS),
});
export type RiderStepInput = z.infer<typeof RiderStepInputSchema>;

export const CancelOrderInputSchema = z.object({
  expectedVersion: Version,
  reason: z.string().trim().min(3, 'Add a short reason').max(300),
});
export type CancelOrderInput = z.infer<typeof CancelOrderInputSchema>;

export const OrderSweepResultSchema = z.object({
  released: z.number().int(),
  ivrCalls: z.number().int(),
  autoCancelled: z.number().int(),
  rushEnded: z.number().int(),
  backOnline: z.number().int(),
  closedAfterOrders: z.number().int(),
});
export type OrderSweepResult = z.infer<typeof OrderSweepResultSchema>;
